import {
  TAG_IMAGE_BUDGET,
  createTagImage,
  type TagImage,
  type TagImageIntent,
} from '@/semantics/TagImage';

/**
 * Getting a picture into the application, from wherever the designer has it.
 *
 * Three routes, because these are the three a person actually uses and any one of them missing reads
 * as the feature being broken:
 *
 * - **Choose a file.** The obvious one, and the only one that was here before.
 * - **Paste.** Overwhelmingly the common case in practice: a reference is nearly always something
 *   just snipped from a browser, a PDF, or a screenshot tool, and it lives on the clipboard and
 *   nowhere else. Making that person save a temporary file first is a whole detour around a feature
 *   that exists to be quick.
 * - **Drop.** Free once the other two exist, and the natural gesture from a folder.
 *
 * All three converge on {@link intakeImages}, so a picture is downscaled, re-encoded and budgeted
 * identically no matter how it arrived — which is what stops "paste" and "attach" from quietly
 * becoming two features with two sets of limits.
 *
 * ## Why re-encode at intake rather than at save
 *
 * A 12-megapixel phone photograph is about 4 MB, and it is going into a document that gets held in
 * memory, autosaved on a debounce, and synced to an account. Shrinking it at the save boundary would
 * mean the editor spends the whole session holding the large one, and the user is told what their
 * picture actually costs only after they have stopped thinking about it. Shrinking on the way in
 * means the number on the chip is the truth from the first moment.
 *
 * 1400 px on the long edge is chosen against what the image models actually consume: their vision
 * encoders tile at far below that, so the pixels past it buy nothing and cost the upload.
 */

/** Everything the browser will hand us that we are willing to read. */
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp'];

export const IMAGE_ACCEPT_ATTRIBUTE = 'image/png,image/jpeg,image/webp,image/gif,image/bmp';

export interface IntakeOptions {
  /** Intent stamped on every picture in this batch. The panel offers to change it afterwards. */
  intent?: TagImageIntent;
  /** How many may be taken in. Defaults to the per-tag budget. */
  limit?: number;
  /** Longest edge, in pixels. Defaults to the budget's. */
  maxEdgePx?: number;
}

export interface IntakeResult {
  images: TagImage[];
  /** Human-readable reasons that files were refused, for the panel to show. Never thrown. */
  rejected: string[];
}

/**
 * Read files into stored images: decode, downscale, re-encode, and stamp with an intent.
 *
 * Never rejects the whole batch for one bad file. Someone dragging in a folder of six pictures, one
 * of which is a PDF, wants the five.
 */
export async function intakeImages(
  files: readonly File[],
  options: IntakeOptions = {},
): Promise<IntakeResult> {
  const limit = Math.max(1, options.limit ?? TAG_IMAGE_BUDGET.perTag);
  const maxEdge = Math.max(64, options.maxEdgePx ?? TAG_IMAGE_BUDGET.maxEdgePx);
  const images: TagImage[] = [];
  const rejected: string[] = [];

  for (const file of files) {
    if (images.length >= limit) {
      rejected.push(`${describe(file)} — only ${limit} can be attached here.`);
      continue;
    }
    if (!isImageFile(file)) {
      rejected.push(`${describe(file)} — not an image this app can read.`);
      continue;
    }
    try {
      const encoded = await encodeImageFile(file, maxEdge);
      images.push(
        createTagImage({
          dataUrl: encoded.dataUrl,
          widthPx: encoded.widthPx,
          heightPx: encoded.heightPx,
          ...(options.intent ? { intent: options.intent } : {}),
          ...(file.name ? { name: file.name } : {}),
        }),
      );
    } catch {
      rejected.push(`${describe(file)} — could not be decoded.`);
    }
  }

  return { images, rejected };
}

export function isImageFile(file: File | null | undefined): file is File {
  if (!file) return false;
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  // Some clipboard and drag sources hand over a blank type. Fall back to the extension rather than
  // refusing, since the decode below is the real test either way.
  return file.type === '' && /\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name ?? '');
}

/**
 * Image files on a clipboard event.
 *
 * `clipboardData.files` alone is not enough: pasting a screenshot on Windows and pasting an image
 * copied from a web page arrive through different paths, and one of them shows up only as an `items`
 * entry with no file list. Both are read here, de-duplicated by name and size, so the panel gets one
 * picture rather than none or two.
 */
export function imagesFromClipboard(event: ClipboardEvent): File[] {
  const data = event.clipboardData;
  if (!data) return [];

  const found: File[] = [...(data.files ?? [])].filter(isImageFile);

  for (const item of [...(data.items ?? [])]) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (!isImageFile(file)) continue;
    const duplicate = found.some(
      (existing) => existing.name === file.name && existing.size === file.size,
    );
    if (!duplicate) found.push(file);
  }

  return found;
}

/** Image files on a drop event. */
export function imagesFromDrop(event: DragEvent): File[] {
  return [...(event.dataTransfer?.files ?? [])].filter(isImageFile);
}

/**
 * Whether a paste should be treated as an image paste at all.
 *
 * Guards the case that matters: someone copying a caption out of a web page and pasting it into the
 * request box gets *text plus* an image in the clipboard on several platforms. If there is usable
 * text and the focus is a text field, the text is what they meant.
 */
export function pasteCarriesText(event: ClipboardEvent): boolean {
  const text = event.clipboardData?.getData('text/plain') ?? '';
  return text.trim().length > 0;
}

/* --------------------------------- Encoding --------------------------------- */

export interface EncodedImage {
  dataUrl: string;
  widthPx: number;
  heightPx: number;
}

/**
 * Decode, fit inside a box, and re-encode as JPEG.
 *
 * JPEG rather than PNG for everything: these are photographs of fences and stonework, where PNG
 * costs three to five times the bytes for detail a render reference does not use. Quality steps down
 * until the result is inside the per-image budget, so a busy photograph gets more compression rather
 * than the document getting a surprise.
 */
export async function encodeImageFile(file: File, maxEdgePx: number): Promise<EncodedImage> {
  const bitmap = await decode(file);
  try {
    const scale = Math.min(1, maxEdgePx / Math.max(bitmap.width, bitmap.height));
    const widthPx = Math.max(1, Math.round(bitmap.width * scale));
    const heightPx = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = widthPx;
    canvas.height = heightPx;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('No 2D context is available.');
    // A white ground under anything transparent: JPEG has no alpha, and the default black turns a
    // cut-out PNG of a gate into a silhouette.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, widthPx, heightPx);
    context.drawImage(bitmap, 0, 0, widthPx, heightPx);

    for (const quality of [0.82, 0.7, 0.58, 0.45]) {
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (payloadBytes(dataUrl) <= TAG_IMAGE_BUDGET.maxBytes) {
        return { dataUrl, widthPx, heightPx };
      }
    }
    // Still over after the last step: hand back the smallest we made rather than failing. The record
    // is honest about its size, and a slightly heavy picture beats no picture.
    const dataUrl = canvas.toDataURL('image/jpeg', 0.45);
    return { dataUrl, widthPx, heightPx };
  } finally {
    if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

type Decoded = ImageBitmap | HTMLImageElement;

/**
 * `createImageBitmap` where it exists, an `<img>` and an object URL where it does not.
 *
 * The fallback is not hypothetical — it is the path Safari took until recently and the path a
 * jsdom-based test takes — and both branches produce something `drawImage` accepts, which is all the
 * caller needs to know.
 */
async function decode(file: File): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('The image could not be decoded.'));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function payloadBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return 0;
  const payload = dataUrl.length - comma - 1;
  return Math.floor((payload * 3) / 4);
}

function describe(file: File): string {
  return file.name && file.name.length > 0 ? file.name : 'A pasted image';
}

/* ------------------------------- Back to blobs ------------------------------- */

/**
 * A stored image as a `Blob`, for the API clients, which take blobs.
 *
 * Synchronous and dependency-free rather than going through `fetch(dataUrl)`: this runs while
 * assembling a render request, and turning a handful of images into a network round trip against
 * one's own data URL would be a strange way to spend a second.
 */
export function tagImageToBlob(image: TagImage): Blob | null {
  const comma = image.dataUrl.indexOf(',');
  if (comma < 0) return null;
  const header = image.dataUrl.slice(0, comma);
  const mediaType = /^data:([^;,]+)/.exec(header)?.[1] ?? 'image/jpeg';
  try {
    const binary = atob(image.dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mediaType });
  } catch {
    return null;
  }
}
